// Vendor
import classnames from 'classnames';
import * as React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { Link } from 'react-scroll';

// Internal
import { Logo } from '@src/svg/Logo';

// Images
import downloadIcon from '@src/data/images/download-icon.png';
import reportPDF from '@src/data/thredup-resaleReport2020.pdf';

// Styles
import styles from './styles.module.css';

interface NavigationProps {
  isOpen: boolean;
  onLinkClick: () => void;
}

type NavSection = Pick<GatsbyTypes.NavigationJson, 'title' | 'links'>;

const getNavigation = graphql`
  query getNavigation {
    allNavigationJson {
      nodes {
        title
        links {
          href
          label
          to
        }
      }
    }
  }
`;

const Navigation: React.FC<NavigationProps> = (props) => {
  const { isOpen, onLinkClick } = props;

  // Hooks
  const navData = useStaticQuery<GatsbyTypes.getNavigationQuery>(getNavigation);

  // Setup
  const navSections = navData.allNavigationJson.nodes;

  // Styles
  const classes = classnames('tup-sidebar', { open: isOpen }, styles.component);
  const classesLink = `text-gray-5 cursor-pointer hover:text-gray-6`;
  const classesListItem = `block pt-2 pb-2`;

  // Markup
  const renderLink = (link?: GatsbyTypes.NavigationJsonLinks) => {
    if (!link) return null;

    const { href, label, to } = link;

    if (to) {
      return (
        <li className={classesListItem} key={to}>
          <Link
            activeClass={styles.active}
            className={classesLink}
            duration={500}
            hashSpy={true}
            onClick={onLinkClick}
            smooth={true}
            spy={true}
            to={to}
          >
            {label}
          </Link>
        </li>
      );
    }

    if (href) {
      return (
        <li className={classesListItem} key={href}>
          <a
            href={href}
            className={classesLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            {label}
          </a>
        </li>
      );
    }

    return null;
  };

  const renderNavSection = (navSection: NavSection) => {
    const { links, title } = navSection;

    if (!links) return null;

    return (
      <div key={title}>
        <h3 className={styles.heading}>{title}</h3>
        <ul>{links.map(renderLink)}</ul>
      </div>
    );
  };

  return (
    <div className={classes}>
      <a
        className={styles.logo}
        href="https://www.thredup.com"
        rel="noopener noreferrer"
        target="_blank"
      >
        <Logo />
      </a>
      {navSections.map(renderNavSection)}
      <ul className="mt-8">
        <li>
          <a
            className="text-gray-5 cursor-pointer hover:text-gray-6 flex items-center"
            href={reportPDF}
            rel="noopener noreferrer"
            target="_blank"
          >
            <span>Download PDF</span>
            <img
              alt="download icon"
              className="inline ml-2"
              src={downloadIcon}
              style={{ maxWidth: 16 }}
            />
          </a>
        </li>
      </ul>
    </div>
  );
};

export { Navigation };
